import React, { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";

import {
  LayoutDashboard,
  SendHorizonal,
  History,
  Users,
  Settings,
  HelpCircle,
  LogOut,
  Bell,
  ChevronDown,
  Building2,
  CreditCard,
  Menu,
  X,
  Wallet,
  Database,
  User,
} from "lucide-react";

import { useAuth } from "./context/AuthContext";
import { getTransactionSummary } from "./api/api";

const NAV_ITEMS = [
  { id: "dashboard",         path: "/",                 icon: LayoutDashboard, label: "Dashboard" },
  { id: "send-money",        path: "/send-money",       icon: SendHorizonal,   label: "Send Money" },
  { id: "history",           path: "/history",          icon: History,         label: "Transaction History" },
  { id: "register-retailer", path: "/b2b/register-retailer",icon: Users,           label: "Retailers" },
  { id: "add-balance",       path: "/b2b/add-balance",  icon: Wallet,          label: "Add Balance" },
  { id: "add-bank",          path: "/b2b/add-bank",     icon: Building2,       label: "Add Bank" },
  { id: "approve-payment",   path: "/b2b/approve-payment", icon: CreditCard, label: "Transaction Log" },
  { id: "settings",          path: "/b2b/settings",    icon: Settings,        label: "Application Settings" },
  { id: "sms-settings",      path: "/b2b/sms-settings", icon: Settings,        label: "SMS Settings" },
  { id: "retailers",          path: "/retailers",         icon:Database,        label: "Retailers Log" },
  { id: "retailer-transactionlog", path: "/retailer-transactionlog", icon:Database, label: "Transaction Log" },
  { id: "admin-balance-ledger", path: "/b2b/admin-balance-ledger", icon: Database, label: "Balance Ledger" },
];

const RETAILER_MENU = ["dashboard", "send-money", "history", "retailer-transactionlog"];
const ADMIN_MENU    = [
  "dashboard",
  "register-retailer",
  "add-balance",
  "add-bank",
  "approve-payment",
  "settings",
  "sms-settings",
  // "retailers",
  "admin-balance-ledger",
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();

  const { user, logout, toast } = useAuth();

  const [userMenuOpen,     setUserMenuOpen]     = useState(false);
  const [mobileOpen,       setMobileOpen]       = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [balance,          setBalance]          = useState(null);
  const [balanceLoading,   setBalanceLoading]   = useState(false);

  // ── Resolve role ──────────────────────────────────────────────────────────
  const userRole       = (user?.role || user?.user_type || "").toString().toLowerCase();
  const isRetailer     = userRole === "retailer";
  console.log("Layout userRole:", userRole, "isRetailer:", isRetailer);
  const allowedMenuIds = isRetailer ? RETAILER_MENU : ADMIN_MENU;

  // ── Fetch wallet balance ──────────────────────────────────────────────────
  const fetchSummary = useCallback(async () => {
    if (!user || !user.token) return;
    setBalanceLoading(true);
    try {
      const payload = { login_id: user.id ?? 5 };
      const body = await getTransactionSummary(payload, user.token);
      if (body?.success) {
        setBalance(body.data?.wallet_balance ?? null);
      }
    } catch (e) {
      console.error("Layout fetchSummary error:", e);
    } finally {
      setBalanceLoading(false);
    }
  }, [user?.token, user?.id]);

  useEffect(() => {
    fetchSummary();
    window.addEventListener("walletUpdated", fetchSummary);
    return () => {
      window.removeEventListener("walletUpdated", fetchSummary);
    };
  }, [fetchSummary]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatINR = (value) => {
    if (value == null) return "-";
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(value);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleSidebarToggle = () => {
    if (window.innerWidth <= 768) {
      setMobileOpen(true);
      return;
    }
    setSidebarCollapsed((current) => !current);
  };

  const currentPage =
    NAV_ITEMS.find((item) => item.path === location.pathname)?.label || "Dashboard";

  return (
    <div className={`app-layout ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="mobile-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileOpen ? "sidebar-open" : ""}`}>

        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">💸</div>
          <div className="sidebar-logo-text">
            SM RUPAY<span>Portal</span>
          </div>
          <button className="mobile-close" onClick={() => setMobileOpen(false)}>
            <X size={18} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {NAV_ITEMS
            .filter((item) => allowedMenuIds.includes(item.id))
            .map((item) => (
              <div
                key={item.id}
                className={`nav-item ${location.pathname === item.path ? "active" : ""}`}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
              >
                <item.icon size={18} />
                {item.label}
              </div>
            ))}
        </nav>

        <div className="nav-logout">
          <div className="nav-item" onClick={handleLogout}>
            <LogOut size={18} />
            Logout
          </div>
        </div>

      </aside>

      {/* Main */}
      <div className="main-content">

        <header
          className="topbar"
          style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", position: "sticky", top: 0, zIndex: 1002 }}
        >

          <div className="mobile-menu" onClick={handleSidebarToggle}>
            <Menu size={22} />
          </div>

          <div className="topbar-title" style={{ marginLeft: 14 }}>{currentPage}</div>

          <div
            className="topbar-right"
            style={{ display: "flex", alignItems: "center", gap: 16, marginLeft: "auto", visibility: "visible", opacity: 1 }}
          >

            {/* Live wallet balance — retailer only */}
            {/* {isRetailer && (
              <div className="topbar-balance">
                <span>Total Balance</span>
                <strong>
                  {balanceLoading ? "Loading..." : `Rs ${formatINR(balance)}`}
                </strong>
              </div>
            )} */}
            <div className="topbar-balance" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <span>Available Balance</span>
              <strong onClick={() => navigate(isRetailer ? "/retailers" : "/b2b/admin-balance-ledger")}>
                {balanceLoading ? "Loading..." : `Rs ${formatINR(balance)}`}
              </strong>
            </div>

            <button className="notif-btn" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <Bell size={20} />
              <span className="notif-badge">3</span>
            </button>

            <div className="dropdown">

              <div
                className="user-info"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="user-avatar">{user?.name?.[0] || "A"}</div>
                <span className="user-name" style={{ display: "inline", fontWeight: 600 }}>{user?.name || "Admin"}</span>
                <ChevronDown size={14} />
              </div>

              {userMenuOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-item" onClick={() => navigate("/view-profile")}>
                    <User size={15} />
                    Profile
                  </div>
                  {/* <div className="dropdown-item" onClick={() => navigate("/settings")}>
                    <Settings size={15} />
                    Settings
                  </div> */}
                  {/* <div className="dropdown-item">
                    <HelpCircle size={15} />
                    Help &amp; Support
                  </div> */}
                  <div className="dropdown-divider" />
                  <div className="dropdown-item danger" onClick={handleLogout}>
                    <LogOut size={15} />
                    Logout
                  </div>
                </div>
              )}

            </div>

          </div>

        </header>

        <main className="page-content">
          <Outlet />
        </main>

      </div>

      {toast && (
        <div className={`toast ${toast.type === "error" ? "error" : ""}`}>
          {toast.message}
        </div>
      )}

    </div>
  );
}